import React, { useId, useMemo } from 'react';
import {
  Checkbox,
  OverflowMenu,
  OverflowMenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { renderDisplay, ColumnDisplay } from './columnDisplays';
import { useCrudConfig } from './provider';
import { CrudSort, cycleSortList } from './urlState';
import { CrudDensity } from './useColumnPrefs';
import type { ReferenceLoad } from './useReferenceOptions';
import { useReferenceOptionsByLoad } from './useReferenceOptions';
import { RowSelection } from './useRowSelection';

export interface CrudColumn<TEntity> {
  key: string;
  headerKey: string;
  /** Omit for a plain value cell. */
  render?: (row: TEntity) => React.ReactNode;
  /**
   * Declarative cell renderer (spec §6.3). Loses to `render` when both are present, so a
   * page can always drop to imperative code for one column.
   */
  display?: ColumnDisplay<TEntity>;
  /**
   * Opt-in, because sorting is server-side: only a real column on the entity's table can be
   * sorted. A computed or joined cell would 400.
   */
  sortable?: boolean;
  /** The column manager refuses to hide this column (reconciliation also enforces it). */
  alwaysVisible?: boolean;
  /** Hidden by default, before the user has expressed any preference. */
  defaultHidden?: boolean;
  /**
   * Serialises this cell for CSV export. Required for a column whose `render` returns
   * markup: React nodes cannot be flattened into a cell, so without this the export falls
   * back to the raw field value and warns.
   */
  toCsv?: (row: TEntity) => string;
}

export interface CrudRowAction<TEntity> {
  labelKey: string;
  onClick: (row: TEntity) => void;
  isDelete?: boolean;
  /**
   * Per-row gate. `canWrite` answers "may this user write at all"; this answers "may this
   * particular row take this action" — the last owner of an organization cannot be removed
   * or demoted however privileged the caller is. Omit it and the action is always enabled,
   * which is what every existing page gets.
   *
   * The item renders disabled rather than hidden: a menu whose entries move between rows is
   * harder to use than one whose entries grey out, and a disabled control still says the
   * action exists.
   */
  disabled?: (row: TEntity) => boolean;
}

export interface CrudTableProps<TEntity> {
  rows: TEntity[];
  columns: CrudColumn<TEntity>[];
  actions: CrudRowAction<TEntity>[];
  emptyState?: React.ReactNode;
  sort?: CrudSort[];
  onSortChange?: (next: CrudSort[]) => void;
  /**
   * Selection state for a leading checkbox column. Omit it and the table renders
   * no checkboxes, which is what every page without bulk actions gets.
   */
  selection?: RowSelection<TEntity>;
  /** Row height. `compact` maps to Carbon's `sm`. Default `comfortable` (`lg`). */
  density?: CrudDensity;
}

/**
 * Carbon's TableHeader.defaultProps.translateWithId hardcodes an English sentence for the
 * hidden description referenced by the sort button's aria-describedby ("Click to sort rows
 * by ... header in ascending order"). React 18.3.1 still applies defaultProps, so without an
 * explicit override every sort header in the product — vi-default included — announces
 * itself in English to a screen reader. `aria-sort` is unaffected; this only replaces the
 * softer, describedby-linked text.
 *
 * Signature and message id read from
 * node_modules/@carbon/react/lib/components/DataTable/TableHeader.js: the sole translation
 * key is `carbon.table.header.icon.description`, called as
 * `t('carbon.table.header.icon.description', { header, sortDirection, isSortHeader, sortStates })`
 * where `sortStates` is `{ NONE, ASC, DESC }` and `header` is the column's rendered children.
 *
 * That `header` is deliberately ignored in favour of the plain label: the children are an
 * ARRAY once the precedence badge joins the label, and i18next stringifies an array, so
 * every sortable header was described as "…by Name,false header…" (or
 * "Name,[object Object]" under a multi-sort).
 */
function buildTranslateWithId(translate: (key: string, vars?: Record<string, unknown>) => string, header: string) {
  return (
    id: string,
    args?: {
      header?: React.ReactNode;
      sortDirection?: string;
      isSortHeader?: boolean;
      sortStates?: { NONE: string; ASC: string; DESC: string };
    }
  ) => {
    if (id !== 'carbon.table.header.icon.description' || !args) {
      return '';
    }
    const { isSortHeader, sortDirection, sortStates } = args;
    if (isSortHeader && sortStates) {
      if (sortDirection === sortStates.NONE) {
        return translate('common.sortAscendingDescription', { header });
      }
      if (sortDirection === sortStates.ASC) {
        return translate('common.sortDescendingDescription', { header });
      }
      return translate('common.sortClearDescription', { header });
    }
    return translate('common.sortAscendingDescription', { header });
  };
}

export function CrudTable<TEntity extends { id: string }>(props: CrudTableProps<TEntity>) {
  const { translate, useTenantParams } = useCrudConfig();
  // eslint-disable-next-line react/hooks -- injected hook, stable per provider mount.
  const params = useTenantParams();
  // Per table, so two CRUD tables on one route never repeat a checkbox id.
  const selectId = useId();

  // The distinct reference loads this table displays, resolved once for the whole table.
  // useColumnPrefs and CrudPage both hand down fresh arrays each render, hence the memo on the
  // column list; the loads themselves are module constants by contract.
  const referenceLoads = useMemo(
    () =>
      Array.from(
        new Set(
          props.columns
            .map((column) => (column.display?.kind === 'reference' ? column.display.load : null))
            .filter((load): load is ReferenceLoad => Boolean(load))
        )
      ),
    [props.columns]
  );
  const references = useReferenceOptionsByLoad(referenceLoads, params);

  return (
    <>
      <Table size={props.density === 'compact' ? 'sm' : 'lg'}>
        <TableHead>
          <TableRow>
            {props.selection && (
              <TableHeader className="crud-table__select">
                <Checkbox
                  id={`${selectId}-all`}
                  labelText={translate('common.selectAll')}
                  hideLabel
                  checked={props.selection.allSelected}
                  indeterminate={props.selection.someSelected && !props.selection.allSelected}
                  onChange={() => props.selection?.toggleAll()}
                />
              </TableHeader>
            )}
            {props.columns.map((column) => {
              if (!column.sortable || !props.onSortChange) {
                return <TableHeader key={column.key}>{translate(column.headerKey)}</TableHeader>;
              }
              const index = (props.sort ?? []).findIndex((entry) => entry.field === column.key);
              const active = index >= 0 ? props.sort![index] : null;
              const onSortChange = props.onSortChange;
              const label = translate(column.headerKey);
              // Precedence, or a two-column sort is invisible.
              const showPrecedence = (props.sort?.length ?? 0) > 1 && index >= 0;
              return (
                <TableHeader
                  key={column.key}
                  isSortable
                  isSortHeader={Boolean(active)}
                  sortDirection={active ? active.direction : 'NONE'}
                  onClick={() => onSortChange(cycleSortList(props.sort ?? [], column.key))}
                  translateWithId={buildTranslateWithId(translate, label)}
                >
                  {label}
                  {/*
                    A bare digit in the button's name ("Name 2") says nothing, so the badge is
                    hidden from assistive tech and the precedence is spoken in words instead.
                    The leading space keeps the words apart from the label in the name.
                  */}
                  {showPrecedence && (
                    <span className="crud-table__sort-order" aria-hidden="true">
                      {index + 1}
                    </span>
                  )}
                  {showPrecedence && (
                    <span className="cds--visually-hidden"> {translate('common.sortPriority', { n: index + 1 })}</span>
                  )}
                </TableHeader>
              );
            })}
            {props.actions.length > 0 && <TableHeader>{translate('common.actions')}</TableHeader>}
          </TableRow>
        </TableHead>
        <TableBody>
          {props.rows.map((row) => (
            <TableRow key={row.id}>
              {props.selection && (
                <TableCell className="crud-table__select">
                  <Checkbox
                    id={`${selectId}-${(row as { id: string }).id}`}
                    labelText={translate('common.selectRow')}
                    hideLabel
                    checked={props.selection.isSelected((row as { id: string }).id)}
                    onChange={() => props.selection?.toggle((row as { id: string }).id)}
                  />
                </TableCell>
              )}
              {props.columns.map((column) => (
                <TableCell key={column.key}>
                  {column.render
                    ? column.render(row)
                    : column.display
                    ? renderDisplay(column.display, row, column.key, references)
                    : ((row as Record<string, unknown>)[column.key] as React.ReactNode)}
                </TableCell>
              ))}
              {props.actions.length > 0 && (
                <TableCell>
                  {/*
                    iconDescription, NOT Carbon's deprecated ariaLabel prop. The icon
                    gets aria-label set to iconDescription, which determines the accessible
                    name. Defaults to "Options", so every row action in the product is
                    currently announced as the built-in default.
                  */}
                  <OverflowMenu size="md" flipped align="bottom-right" iconDescription={translate('actions.title')}>
                    {props.actions.map((action) => (
                      <OverflowMenuItem
                        key={action.labelKey}
                        requireTitle
                        hasDivider={action.isDelete}
                        isDelete={action.isDelete}
                        itemText={translate(action.labelKey)}
                        disabled={action.disabled?.(row) ?? false}
                        onClick={() => action.onClick(row)}
                      />
                    ))}
                  </OverflowMenu>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {props.rows.length === 0 && props.emptyState}
    </>
  );
}
